import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, getDocs, limit, addDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged, signInWithCustomToken, signOut, getIdTokenResult } from 'firebase/auth';
import { auth, db } from '../config/firebase';

const AuthContext = createContext();

// Default timeout values (in milliseconds)
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_TIMEOUT = 5 * 60 * 1000; // 5 minutes warning before logout
const ROLE_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes - refresh role/permissions periodically
const SESSION_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours - force re-authentication daily
const SESSION_TIMESTAMP_KEY = 'sessionTimestamp';

export function AuthProvider({ children }) {
  const [currentAgent, setCurrentAgent] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verificationStage, setVerificationStage] = useState('email'); // 'email' | 'otp' | 'authenticated'
  const [verificationId, setVerificationId] = useState('');
  const [agentDocId, setAgentDocId] = useState('');
  const [retriesLeft, setRetriesLeft] = useState(2);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const authInitializedRef = useRef(false);
  const roleRefreshTimerRef = useRef(null);
  const tokenValidationTimerRef = useRef(null);

  const logout = useCallback(() => {
    setCurrentAgent(null);
    setIsAuthenticated(false);
    setVerificationStage('email');
    setVerificationId('');
    setAgentDocId('');
    setRetriesLeft(2);
    setShowIdleWarning(false);
    setTokenValid(false);
    setTokenExpiry(null);
    localStorage.removeItem('currentAgent');
    localStorage.removeItem(SESSION_TIMESTAMP_KEY);

    try {
      signOut(auth).catch(() => {});
    } catch {}
    
    // Clear any pending timers
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (roleRefreshTimerRef.current) clearTimeout(roleRefreshTimerRef.current);
    if (tokenValidationTimerRef.current) clearInterval(tokenValidationTimerRef.current);
    idleTimerRef.current = null;
    warningTimerRef.current = null;
    roleRefreshTimerRef.current = null;
    tokenValidationTimerRef.current = null;
  }, []);

  // Reset timers on user activity
  const resetIdleTimer = useCallback(() => {
    if (showIdleWarning) {
      setShowIdleWarning(false);
    }
    
    // Clear existing timers
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    idleTimerRef.current = null;
    warningTimerRef.current = null;

    // Only set timers if user is authenticated
    if (isAuthenticated) {
      // Set warning timer
      const newWarningTimer = setTimeout(() => {
        setShowIdleWarning(true);
        // Set logout timer after warning
        const newIdleTimer = setTimeout(() => {
          logout();
        }, WARNING_TIMEOUT);
        idleTimerRef.current = newIdleTimer;
      }, IDLE_TIMEOUT - WARNING_TIMEOUT);
      
      warningTimerRef.current = newWarningTimer;
    }
  }, [isAuthenticated, showIdleWarning, logout]);

  // Set up event listeners for user activity
    useEffect(() => {
      if (isAuthenticated) {
        resetIdleTimer();
        
        const activityEvents = [
          { event: 'mousedown', passive: false },
          { event: 'mousemove', passive: false },
          { event: 'keypress', passive: false },
          { event: 'scroll', passive: true },
          { event: 'touchstart', passive: true }  // Fixes the warning
        ];
        
        const resetTimerOnActivity = () => resetIdleTimer();
        
        activityEvents.forEach(({ event, passive }) => {
          window.addEventListener(event, resetTimerOnActivity, { passive });
        });

        return () => {
          activityEvents.forEach(({ event, passive }) => {
            window.removeEventListener(event, resetTimerOnActivity, { passive });
          });
          if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
          if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
          idleTimerRef.current = null;
          warningTimerRef.current = null;
        };
      }
    }, [isAuthenticated, resetIdleTimer]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (roleRefreshTimerRef.current) clearTimeout(roleRefreshTimerRef.current);
      if (tokenValidationTimerRef.current) clearInterval(tokenValidationTimerRef.current);
      idleTimerRef.current = null;
      warningTimerRef.current = null;
      roleRefreshTimerRef.current = null;
      tokenValidationTimerRef.current = null;
    };
  }, []);

  // Check if token is valid and not expired
  const checkTokenValidity = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) return false;
      
      const tokenResult = await getIdTokenResult(user);
      const expiryTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      const isValid = expiryTime > now;
      
      setTokenExpiry(expiryTime);
      setTokenValid(isValid);
      
      // Auto-refresh if token will expire within 5 minutes
      const timeUntilExpiry = expiryTime - now;
      if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
        await user.getIdToken(true); // Force refresh
        return checkTokenValidity(); // Re-check after refresh
      }
      
      return isValid;
    } catch (error) {
      setTokenValid(false);
      setTokenExpiry(null);
      return false;
    }
  }, []);

  // Periodic token validation (every 30 seconds)
  useEffect(() => {
    if (!isAuthenticated) {
      if (tokenValidationTimerRef.current) {
        clearInterval(tokenValidationTimerRef.current);
        tokenValidationTimerRef.current = null;
      }
      return;
    }
    
    // Initial check
    checkTokenValidity();
    
    // Set up periodic validation
    const validateInterval = setInterval(async () => {
      const valid = await checkTokenValidity();
      if (!valid) {
        // Token invalid - force logout
        logout();
      }
    }, 30000); // Check every 30 seconds
    
    tokenValidationTimerRef.current = validateInterval;
    
    return () => {
      clearInterval(validateInterval);
      tokenValidationTimerRef.current = null;
    };
  }, [isAuthenticated, checkTokenValidity, logout]);

  // Refresh agent data from server to get updated roles/permissions
  const refreshAgentData = useCallback(async () => {
    if (!currentAgent?.id || !isAuthenticated) return;

    try {
      const agentsRef = collection(db, 'plsss_agents');
      const agentDoc = await getDocs(query(agentsRef, where('__name__', '==', currentAgent.id), limit(1)));
      
      if (!agentDoc.empty) {
        const docData = agentDoc.docs[0].data();
        const agentPermissions = docData.apps?.agents || { allowed: true, role: 'user' };
        
        // Check if user is allowed to access the system
        if (agentPermissions.allowed === false) {
          logout();
          return;
        }
        
        const updatedAgentData = {
          ...currentAgent,
          ...docData,
          id: agentDoc.docs[0].id,
          documentId: agentDoc.docs[0].id,
          code: docData.code || docData.agent_id || agentDoc.docs[0].id,
          defaultCountry: docData.defaultCountry || 'BHD',
          role: agentPermissions.role || 'user',
          permissions: agentPermissions
        };

        // Check if role or permissions changed
        const roleChanged = currentAgent.role !== updatedAgentData.role;
        const permissionsChanged = JSON.stringify(currentAgent.permissions) !== JSON.stringify(updatedAgentData.permissions);

        if (roleChanged || permissionsChanged) {
          setCurrentAgent(updatedAgentData);
          localStorage.setItem('currentAgent', JSON.stringify(updatedAgentData));
        }
      } else {
        // Agent document no longer exists - account may have been deleted
        logout();
      }
    } catch (error) {
      // Don't logout on network errors, only log
    }
  }, [currentAgent, isAuthenticated, logout]);

  // Set up periodic role refresh
  useEffect(() => {
    if (!isAuthenticated || !currentAgent?.id) {
      // Clear timer if not authenticated
      if (roleRefreshTimerRef.current) {
        clearTimeout(roleRefreshTimerRef.current);
        roleRefreshTimerRef.current = null;
      }
      return;
    }

    // Initial refresh after a short delay
    const initialRefresh = setTimeout(() => {
      refreshAgentData();
    }, 1000); // 1 second after authentication

    // Set up periodic refresh
    const periodicRefresh = setInterval(() => {
      refreshAgentData();
    }, ROLE_REFRESH_INTERVAL);

    roleRefreshTimerRef.current = periodicRefresh;

    return () => {
      clearTimeout(initialRefresh);
      clearInterval(periodicRefresh);
      roleRefreshTimerRef.current = null;
    };
  }, [isAuthenticated, currentAgent?.id, refreshAgentData]);

  // Check for saved session on mount
  useEffect(() => {
    const savedAgent = localStorage.getItem('currentAgent');
    const sessionTimestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
    
    if (savedAgent) {
      try {
        const agent = JSON.parse(savedAgent);
        
        // Check if session is older than 24 hours
        const sessionAge = sessionTimestamp ? Date.now() - parseInt(sessionTimestamp) : Infinity;
        const isSessionExpired = sessionAge > SESSION_MAX_AGE;
        
        if (isSessionExpired) {
          // Session expired, force re-authentication
          localStorage.removeItem('currentAgent');
          localStorage.removeItem(SESSION_TIMESTAMP_KEY);
          setCurrentAgent(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        // Verify account still exists before restoring session
        const verifyAccount = async () => {
          try {
            const agentsRef = collection(db, 'plsss_agents');
            const agentDoc = await getDocs(query(agentsRef, where('__name__', '==', agent.id), limit(1)));
            
            if (agentDoc.empty) {
              // Account no longer exists, force logout
              localStorage.removeItem('currentAgent');
              localStorage.removeItem(SESSION_TIMESTAMP_KEY);
              setCurrentAgent(null);
              setIsAuthenticated(false);
            } else {
              // Account exists, restore session
              setCurrentAgent(agent);
              setIsAuthenticated(true);
            }
          } catch (error) {
            // On error, don't restore session for security
            localStorage.removeItem('currentAgent');
            localStorage.removeItem(SESSION_TIMESTAMP_KEY);
            setCurrentAgent(null);
            setIsAuthenticated(false);
          } finally {
            setLoading(false);
          }
        };
        
        verifyAccount();
      } catch (error) {
        localStorage.removeItem('currentAgent');
        localStorage.removeItem(SESSION_TIMESTAMP_KEY);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Reconcile UI session with Firebase Auth session
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      authInitializedRef.current = true;
      const savedAgent = localStorage.getItem('currentAgent');
      if (authInitializedRef.current && savedAgent && !user) {
        // Do not allow UI-authenticated but Firebase-unauthenticated states
        logout();
      }
    });
    return () => unsubscribe();
  }, [logout]);

  // Cloud function endpoints
  const PROJECT_ID = 'plsss-project';
  const REGION = 'europe-west3';
  const FN_BASE = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net`;
  const OTP_AUTH_URL = `${FN_BASE}/otpAuth`;
  const CREATE_AGENT_SESSION_URL = `${FN_BASE}/createAgentSession`;

  // Generate correlation ID for session tracking
  const generateCorrelationId = () => {
    if (crypto && crypto.getRandomValues) {
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);
      buf[6] = (buf[6] & 0x0f) | 0x40;
      buf[8] = (buf[8] & 0x3f) | 0x80;
      const bth = Array.from(buf).map((b) => b.toString(16).padStart(2, '0'));
      return (
        bth[0] + bth[1] + bth[2] + bth[3] + '-' +
        bth[4] + bth[5] + '-' + bth[6] + bth[7] + '-' +
        bth[8] + bth[9] + '-' + bth[10] + bth[11] + bth[12] + bth[13] + bth[14] + bth[15]
      );
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const requestOTP = async (email) => {
    try {
      setLoading(true);

      // Request OTP via cloud function
      const correlationId = generateCorrelationId();
      const res = await fetch(OTP_AUTH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'request',
          email: email,
          reference: { type: 'otp', correlationId },
        }),
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => '');
        throw new Error(errorText || res.statusText);
      }

      const responseData = await res.json().catch(() => ({}));
      setAgentDocId(responseData.agentId || '');
      setVerificationId(responseData.verificationId || '');
      setVerificationStage('otp');
      setRetriesLeft(2);
      
      return { success: true, stage: 'otp', message: 'OTP sent to your email' };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to send OTP. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (otpCode) => {
    try {
      setLoading(true);

      if (!/^\d{6}$/.test(otpCode)) {
        return { success: false, error: 'Please enter a valid 6-digit code' };
      }

      const client = {
        ip: '',
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
      };

      const res = await fetch(OTP_AUTH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'verify',
          agentId: agentDocId,
          verificationId,
          code: otpCode,
          client,
          reference: { type: 'otp', agentId: agentDocId, verificationId },
        }),
      });

      if (res.ok) {
        // Upgrade to Firebase Auth session (custom token) without touching otpAuth
        const sessionRes = await fetch(CREATE_AGENT_SESSION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agentId: agentDocId, verificationId }),
        });

        if (!sessionRes.ok) {
          const sessionErr = await sessionRes.text().catch(() => '');
          throw new Error(sessionErr || 'Failed to create authenticated session');
        }

        const sessionData = await sessionRes.json().catch(() => ({}));
        if (!sessionData.firebaseCustomToken) {
          throw new Error('Missing firebaseCustomToken');
        }

        await signInWithCustomToken(auth, sessionData.firebaseCustomToken);

        // OTP verified successfully, fetch complete agent data
        const agentsRef = collection(db, 'plsss_agents');
        const agentDoc = await getDocs(query(agentsRef, where('__name__', '==', agentDocId), limit(1)));
        
        if (!agentDoc.empty) {
          const docData = agentDoc.docs[0].data();
          const agentPermissions = docData.apps?.agents || { allowed: true, role: 'user' };
          
          // Check if user is allowed to access the system
          if (agentPermissions.allowed === false) {
            return { success: false, error: 'Your account has been disabled. Please contact support.' };
          }
          
          const agentData = {
            ...docData,
            id: agentDoc.docs[0].id,
            documentId: agentDoc.docs[0].id,
            code: docData.code || docData.agent_id || agentDoc.docs[0].id,
            defaultCountry: docData.defaultCountry || 'BHD',
            role: agentPermissions.role || 'user',
            permissions: agentPermissions
          };
          
          // console.log('Agent data loaded:', agentData);

          setCurrentAgent(agentData);
          setIsAuthenticated(true);
          setVerificationStage('authenticated');
          localStorage.setItem('currentAgent', JSON.stringify(agentData));
          localStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());
          
          return { success: true, agent: agentData };
        }
      }

      // Handle failed verification
      const errorText = await res.text().catch(() => '');
      const newRetriesLeft = retriesLeft - 1;
      setRetriesLeft(newRetriesLeft);

      if (newRetriesLeft <= 0) {
        // Log failed attempt
        await logFailedAttempt('Too many incorrect OTP attempts');
        setVerificationStage('email');
        return { success: false, error: 'Too many incorrect attempts. Please start over.' };
      }

      return { 
        success: false, 
        error: errorText || 'Invalid verification code',
        retriesLeft: newRetriesLeft 
      };
    } catch (error) {
      return { success: false, error: error.message || 'Verification failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const logFailedAttempt = async (reason) => {
    try {
      if (!agentDocId) return;
      const failedCol = collection(db, 'plsss_agents', agentDocId, 'failed_logins');
      await addDoc(failedCol, {
        reason,
        ip: '',
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      // Failed to log failed login
    }
  };

  const value = {
    currentAgent,
    isAuthenticated,
    loading,
    verificationStage,
    retriesLeft,
    showIdleWarning,
    tokenValid,
    tokenExpiry,
    checkTokenValidity,
    requestOTP,
    verifyOTP,
    logout,
    resetIdleTimer, // Expose this to allow manual reset from UI if needed
    refreshAgentData // Expose this to allow manual role refresh from UI if needed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
